/**
 * Archive Settings — Memorial defaults + links to test memorial workspace.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Flame, ExternalLink, Copy, QrCode, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { getApiBase } from '../../utils/apiBase';
import { getDisplayName, isGenealogicalPerson } from '../../utils/companionProfiles';
import {
  getMemorialPublicUrl,
  isMemorialPublished,
  memorialManageHref,
} from '../../utils/memorial';

const API = getApiBase();

const DEFAULTS = [
  { label: 'Default visibility', value: 'Private' },
  { label: 'Tribute moderation', value: 'On' },
  { label: 'Public links', value: 'After publish' },
  { label: 'QR codes', value: 'Published only' },
];

export function ArchiveSettingsMemorialSection({ people = [], treeId }) {
  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState([]);

  const genealogical = useMemo(
    () => (people || []).filter(isGenealogicalPerson).slice(0, 40),
    [people]
  );

  useEffect(() => {
    if (!genealogical.length) {
      setPublished([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const results = await Promise.allSettled(
        genealogical.map(async (person) => {
          const { data } = await axios.get(`${API}/memorials-api/by-person/${person.id}`);
          const memorial = data?.memorial;
          if (!isMemorialPublished(memorial)) return null;
          return {
            person,
            memorial,
            publicUrl: getMemorialPublicUrl(data?.public_url, memorial?.slug),
          };
        })
      );
      if (!cancelled) {
        setPublished(results.map((r) => (r.status === 'fulfilled' ? r.value : null)).filter(Boolean));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [genealogical, treeId]);

  const copyLink = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Memorial link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  return (
    <div className="space-y-5" data-testid="archive-settings-memorials">
      <div className="grid gap-2 sm:grid-cols-2">
        {DEFAULTS.map(({ label, value }) => (
          <div
            key={label}
            className="flex items-center justify-between rounded-lg bg-muted/15 px-3 py-2.5 text-sm"
          >
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium text-foreground/85">{value}</span>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border/35 bg-muted/10 p-4">
        <h3 className="text-sm font-medium">Memorial workspaces</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Open a person&apos;s memorial workspace to draft, publish, and manage QR links.
        </p>

        {genealogical.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Add people to the tree first.</p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {genealogical.map((person) => {
              const name = getDisplayName(person);
              const href = memorialManageHref(person);
              return (
                <li
                  key={person.id}
                  className="flex flex-col gap-2 rounded-lg bg-background/70 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <span className="text-sm">{name}</span>
                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-full shrink-0" asChild>
                    <Link to={href} data-testid={`memorial-open-${person.id}`}>
                      <Flame className="mr-1.5 h-3 w-3" />
                      Open memorial
                      <ArrowRight className="ml-1 h-3 w-3 opacity-60" />
                    </Link>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-border/35 bg-muted/10 p-4">
        <h3 className="text-sm font-medium">Published pages</h3>
        {loading ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Checking…
          </p>
        ) : published.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            None yet — create a memorial from a person profile, then publish when ready.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {published.map(({ person, publicUrl }) => (
              <li key={person.id} className="rounded-lg bg-background/70 px-3 py-2.5 space-y-2">
                <p className="text-sm font-medium">{getDisplayName(person)}</p>
                <p className="truncate text-xs text-muted-foreground">{publicUrl}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-full" asChild>
                    <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-1 h-3 w-3" />
                      View page
                    </a>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-full"
                    onClick={() => copyLink(publicUrl)}
                  >
                    <Copy className="mr-1 h-3 w-3" />
                    Copy link
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 rounded-full" asChild>
                    <Link to={memorialManageHref(person)}>
                      <QrCode className="mr-1 h-3 w-3" />
                      View QR
                    </Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ArchiveSettingsMemorialSection;
